import { useEffect, useState } from "react";
import { LeaveAPI } from "../../api/leave";

export default function MyLeavePage() {
  const [types, setTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [allowances, setAllowances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    leave_type_id: "",
    start_at: "",
    end_at: "",
    is_all_day: true,
    notes: "",
  });

  async function loadData() {
    try {
      setLoading(true);
      const [typesData, requestsData, allowancesData] = await Promise.all([
        LeaveAPI.listTypes(),
        LeaveAPI.listMyRequests(),
        LeaveAPI.listMyAllowances(),
      ]);

      setTypes(Array.isArray(typesData) ? typesData : []);
      setRequests(Array.isArray(requestsData) ? requestsData : []);
      setAllowances(Array.isArray(allowancesData) ? allowancesData : []);
      setError("");
    } catch (err) {
      setError(err.message || "Failed to load leave data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setSaving(true);
      await LeaveAPI.createRequest({
        leave_type_id: Number(form.leave_type_id),
        start_at: form.start_at,
        end_at: form.end_at,
        is_all_day: form.is_all_day,
        notes: form.notes,
      });

      setForm({
        leave_type_id: "",
        start_at: "",
        end_at: "",
        is_all_day: true,
        notes: "",
      });

      await loadData();
    } catch (err) {
      setError(err.message || "Failed to create leave request");
    } finally {
      setSaving(false);
    }
  }

  async function handleCancel(id) {
    if (!window.confirm("Cancel this leave request?")) return;

    try {
      await LeaveAPI.cancelRequest(id);
      await loadData();
    } catch (err) {
      setError(err.message || "Failed to cancel leave request");
    }
  }

  if (loading) {
    return <div className="p-4">Loading leave…</div>;
  }

  return (
    <div className="p-4 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">My Leave</h1>
        <p className="text-sm text-gray-600 mt-1">
          Request leave and view your balances.
        </p>
      </div>

      {error ? (
        <div className="border border-red-200 bg-red-50 text-red-700 rounded p-3 text-sm">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border rounded bg-white p-4">
          <h2 className="text-lg font-semibold mb-3">Request Leave</h2>

          <form className="space-y-3" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium mb-1">Leave type</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={form.leave_type_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, leave_type_id: e.target.value }))
                }
                required
              >
                <option value="">Select leave type</option>
                {types.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Start</label>
              <input
                type={form.is_all_day ? "date" : "datetime-local"}
                className="w-full border rounded px-3 py-2"
                value={form.start_at}
                onChange={(e) =>
                  setForm((f) => ({ ...f, start_at: e.target.value }))
                }
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">End</label>
              <input
                type={form.is_all_day ? "date" : "datetime-local"}
                className="w-full border rounded px-3 py-2"
                value={form.end_at}
                onChange={(e) =>
                  setForm((f) => ({ ...f, end_at: e.target.value }))
                }
                required
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_all_day}
                onChange={(e) =>
                  setForm((f) => {
                    const isAllDay = e.target.checked;

                    return {
                      ...f,
                      is_all_day: isAllDay,
                      start_at: isAllDay && f.start_at ? f.start_at.slice(0, 10) : f.start_at,
                      end_at: isAllDay && f.end_at ? f.end_at.slice(0, 10) : f.end_at,
                    };
                  })
                }
              />
              All day
            </label>

            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                className="w-full border rounded px-3 py-2"
                rows={3}
                value={form.notes}
                onChange={(e) =>
                  setForm((f) => ({ ...f, notes: e.target.value }))
                }
              />
            </div>

            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              {saving ? "Submitting..." : "Submit request"}
            </button>
          </form>
        </div>

        <div className="border rounded bg-white p-4">
          <h2 className="text-lg font-semibold mb-3">My Allowances</h2>

          {allowances.length === 0 ? (
            <div className="text-sm text-gray-500">No allowances found.</div>
          ) : (
            <div className="space-y-3">
              {allowances.map((item) => (
                <div key={item.id} className="border rounded p-3">
                  <div className="font-medium">{item.leave_type_name}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Allowance: {item.allowance_days}
                  </div>
                  <div className="text-sm text-gray-600">
                    Carry over: {item.carry_over_days}
                  </div>
                  <div className="text-sm text-gray-600">
                    Used: {item.used_days}
                  </div>
                  <div className="text-sm font-medium mt-1">
                    Remaining: {item.remaining_days}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="border rounded bg-white p-4">
        <h2 className="text-lg font-semibold mb-3">My Requests</h2>

        {requests.length === 0 ? (
          <div className="text-sm text-gray-500">No leave requests yet.</div>
        ) : (
          <div className="space-y-3">
            {requests.map((request) => (
              <div key={request.id} className="border rounded p-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium">{request.leave_type_name}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {request.is_all_day ? (
                        <>
                          All day: {new Date(request.start_at).toLocaleDateString()}{" "}
                          {new Date(request.start_at).toDateString() !==
                          new Date(request.end_at).toDateString()
                            ? `→ ${new Date(request.end_at).toLocaleDateString()}`
                            : ""}
                        </>
                      ) : (
                        <>
                          {new Date(request.start_at).toLocaleString()} →{" "}
                          {new Date(request.end_at).toLocaleString()}
                        </>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Status: {request.status}
                    </div>
                    {request.notes ? (
                      <div className="text-sm text-gray-600 mt-1">
                        Notes: {request.notes}
                      </div>
                    ) : null}
                    {request.rejection_reason ? (
                      <div className="text-sm text-red-600 mt-1">
                        Rejection reason: {request.rejection_reason}
                      </div>
                    ) : null}
                  </div>

                  {request.status === "pending" || request.status === "approved" ? (
                    <button
                      className="px-3 py-1 rounded bg-gray-200"
                      onClick={() => handleCancel(request.id)}
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}